/**
 * Shared Zod schemas for dataset references in LLM-facing tool input schemas.
 *
 * Every dataset-producing tool stages its result under an `outputDataset`; every
 * dataset-consuming tool receives a `inputDataset` that points back at one of
 * those staged datasets. These primitives centralize that vocabulary so tool
 * schemas don't redeclare the same `z.string().describe(...)` slot with
 * drifting descriptions. Call sites override via Zod's native `.describe(...)`
 * / `.optional()` / `.min(...)` when they need tool-specific framing.
 *
 * Descriptions are intentionally tool-agnostic. Cross-tool references
 * ("use `transformDataset` to filter first", "paired with `getDatapoints`")
 * belong in each tool's `ToolGuidance` / `instructions` block — that's where
 * `.mention` resolves without the createTool-time import cycle that plagues
 * schema modules.
 */
import { z } from 'zod';

/** LLM-facing input: stage the produced dataset under this key. */
export const outputDatasetSchema = z
  .string()
  .describe('Unique key under which the produced dataset is staged in chat history for downstream tools to reference.');

/** LLM-facing input: reference a dataset previously staged by an upstream tool. */
export const inputDatasetSchema = z
  .string()
  .describe('Reference to a dataset previously staged in chat history by an upstream data-producing tool.');

/**
 * Compound reference to project a column from a previously staged dataset.
 *
 * Shared by filter-leaf `sourceRef` (value chaining for `in`/`notIn` ops) and
 * chart-series overlays. Both fields enforce `min(1)` to reject empty-string
 * references. Call sites re-describe fields where domain-specific framing
 * matters.
 */
export const sourceRefSchema = z.object({
  inputDataset: inputDatasetSchema.min(1),
  column: z.string().min(1).describe('Column to project from the referenced dataset.'),
});
